import { Button, Flex, IconFont, Input, pointer, SegmentedControl, Text } from '@byecode/ui'
import { type BreakPoint, DeviceEnum } from '@lighthouse/core'
import { ErrorMessage, TitleInContentModal } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { find } from 'rambda'
import type { FormEvent } from 'react'
import React, { useCallback } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import styled, { css } from 'styled-components'

import { BreakPointPreinstall, DeviceTypeOption } from '@/constants/breakpointType'

import { DeviceIconPicker } from './DeviceIconPicker'

const SCxContainer = styled.form`
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding-bottom: 32px;
`

const SCxContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 32px 0 32px;
`

const SCxCard = styled.div<{ active?: boolean }>`
    height: 102px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex: 1;
    background-color: var(--color-gray-100);
    color: var(--color-gray-400);
    border-radius: 8px;

    ${pointer}

    &:hover {
        background-color: ${({ active }) => (active ? 'var(--color-main-tint)' : 'var(--color-gray-200)')};
    }
    ${({ active }) =>
        active &&
        css`
            background-color: var(--color-main-tint);
            color: var(--color-main);
        `}
`

const SCxInput = styled(Input)<{ error?: boolean }>`
    border: ${({ error }) => `1px solid ${error ? '#d88987' : 'transparent'}`};
    &:focus {
        border-color: var(--color-main);
        box-shadow: 0px 0px 0px 2px rgba(42, 192, 126, 0.15);
    }
`

const SCxFooter = styled(Flex)`
    padding: 0 32px;
`

type BreakPointFormValue = Omit<BreakPoint, 'id'> & { id?: string }

interface BreakPointFormProps {
    open: boolean
    loading: boolean
    defaultValues?: BreakPointFormValue
    onClose: () => void
    onCreate: (val: BreakPoint) => void
    onUpdate: (val: BreakPoint) => void
}

const BreakPointFormInit = {
    ...BreakPointPreinstall[3],
    icon: 'ScreenM'
}

export const BreakPointForm: React.FC<BreakPointFormProps> = ({
    open,
    loading,
    defaultValues = BreakPointFormInit,
    onCreate,
    onUpdate,
    onClose
}) => {
    const methods = useForm<BreakPointFormValue>({
        mode: 'onSubmit',
        defaultValues
    })

    const {
        control,
        reset,
        handleSubmit,
        formState: { errors }
    } = methods

    const device = useWatch({ control, name: 'device' })

    const handleConfirm = useCallback(
        (data: BreakPointFormValue) => {
            if (data.id) {
                onUpdate({ ...data, id: data.id })
                return
            }
            const id = nanoid()
            onCreate({ ...data, id })
        },
        [onCreate, onUpdate]
    )

    const handlePreinstall = useCallback(
        (val: string) => {
            const init = find(item => item.device === val, BreakPointPreinstall)
            if (init?.device === DeviceEnum.CUSTOM) {
                reset({
                    id: defaultValues?.id,
                    ...init,
                    icon: 'ScreenM'
                })
                return
            }
            reset({ id: defaultValues?.id, ...init })
        },
        [defaultValues?.id, reset]
    )

    const handleFormSubmit = useCallback(
        (ev: FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleConfirm)()
        },
        [handleConfirm, handleSubmit]
    )

    return (
        <TitleInContentModal
            width={505}
            title={defaultValues.id ? '编辑断点' : '添加断点'}
            description={
                <Text size={14} color="var(--color-gray-400)">
                    断点是用于确定网页布局或样式发生变化的特定屏幕宽度值。
                </Text>
            }
            open={open}
            withCloseIcon
            onClose={onClose}
        >
            <SCxContainer onSubmit={handleFormSubmit}>
                <SCxContent>
                    <Flex gap={8} direction="column">
                        <Text size={14}>断点类型</Text>
                        <Flex justifyContent="space-between" alignItems="center" gap={12}>
                            {BreakPointPreinstall.map(item => (
                                <SCxCard key={item.icon} active={device === item.device} onClick={() => handlePreinstall(item.device)}>
                                    <IconFont type={item.icon} size={24} />
                                    <Text size={14} weight={device === item.device ? 600 : 400}>
                                        {item.label}
                                    </Text>
                                    <Text size={14}>{Number.isNaN(item.width) ? '-' : item.width}</Text>
                                </SCxCard>
                            ))}
                        </Flex>
                    </Flex>
                    {device === DeviceEnum.CUSTOM && (
                        <>
                            <Flex gap={8} direction="column">
                                <Text size={14}>标题</Text>
                                <Flex gap={8}>
                                    <Controller
                                        name="icon"
                                        control={control}
                                        rules={{ required: '图标不能为空' }}
                                        render={({ field }) => <DeviceIconPicker value={field.value || ''} onChange={field.onChange} />}
                                    />
                                    <ErrorMessage name="name" errors={errors}>
                                        <Controller
                                            name="name"
                                            control={control}
                                            rules={{ required: '标题不能为空' }}
                                            render={({ field }) => (
                                                <SCxInput
                                                    style={{ flex: 1 }}
                                                    size="lg"
                                                    error={!!errors.name}
                                                    placeholder="请输入"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                    </ErrorMessage>
                                </Flex>
                            </Flex>
                            <Flex gap={16}>
                                <Flex gap={8} direction="column" style={{ flex: 1 }}>
                                    <Text size={14}>宽度</Text>
                                    <ErrorMessage name="width" errors={errors}>
                                        <Controller
                                            name="width"
                                            control={control}
                                            rules={{
                                                required: '请输入',
                                                validate(val) {
                                                    if (!/^\d+$/.test(String(val))) {
                                                        return '只能输入正整数'
                                                    }
                                                    if (Number(val) > 1000000) {
                                                        return '宽度要求小于1000000'
                                                    }
                                                    return Number.isNaN(val) ? '请输入' : true
                                                }
                                            }}
                                            render={({ field }) => (
                                                <SCxInput
                                                    style={{ flex: 1 }}
                                                    size="lg"
                                                    error={!!errors.width}
                                                    type="number"
                                                    placeholder="请输入"
                                                    value={Number.isNaN(field.value) ? '' : field.value}
                                                    onChange={ev => {
                                                        const inputValue = ev.target.value
                                                        field.onChange(Number(inputValue))
                                                    }}
                                                />
                                            )}
                                        />
                                    </ErrorMessage>
                                </Flex>
                                <Flex gap={8} direction="column" style={{ flex: 1 }}>
                                    <Text size={14}>客户端类型</Text>
                                    <Controller
                                        name="type"
                                        control={control}
                                        rules={{ required: '请输入' }}
                                        render={({ field }) => (
                                            <SegmentedControl
                                                fullWidth
                                                size="lg"
                                                value={field.value}
                                                onChange={field.onChange}
                                                data={DeviceTypeOption}
                                            />
                                        )}
                                    />
                                </Flex>
                            </Flex>
                        </>
                    )}
                </SCxContent>
                <SCxFooter gap={12}>
                    <Button block size="lg" onClick={onClose}>
                        取消
                    </Button>
                    <Button loading={loading} block size="lg" htmlType="submit" type="primary">
                        确认
                    </Button>
                </SCxFooter>
            </SCxContainer>
        </TitleInContentModal>
    )
}
